import { useState } from 'react';
import { useEmployeeService, useEmployeeNamesService } from 'domain/shared/employee';
import { EXTERNAL_USER_PREFIX } from '../../risks/RiskOwnerDropdown';
import {
  generateBusinessOutcomesTable,
  generateCRTable,
  generateMilestonesTable,
  generateOldViewMilestonesTable,
  generateRiskTable,
} from '../mdEditor/utills';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { PlaceholderValues } from 'framework/markdownEditor';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { RisksType, useNewProjectService } from 'domain/shared/newProject';

export const useCustomerReportPlaceholders = (projectId: string | undefined) => {
  const [placeholderValues, setPlaceholderValues] = useState<PlaceholderValues>({});
  const { getFullNameWithoutMaiden } = useEmployeeService();
  const { getEmployeeNamesByAliases } = useEmployeeNamesService();
  const { getById } = useNewProjectService();
  const { getToday } = useDateService();
  const { getUserAlias } = useAuthenticatedUserService();
  const getAliasFullName = async (alias: string | undefined, defaultValue: string = '') => {
    if (!alias) return defaultValue;
    return getEmployeeNamesByAliases([alias])
      .then(response => getFullNameWithoutMaiden(response?.[0]?.nameEn))
      .catch(() => {
        return defaultValue;
      });
  };

  const calculatePlaceholders = async (data: any, templateText?: string) => {
    const projectData = projectId ? await getById(projectId) : {};

    const processedRisks = await Promise.all(
      (data.risks || []).map(async (risk: RisksType) => {
        let riskOwnerName = '';

        if (risk.riskOwner) {
          const isExternal = risk.riskOwner.startsWith(EXTERNAL_USER_PREFIX);

          if (isExternal) {
            riskOwnerName = risk.riskOwner.slice(EXTERNAL_USER_PREFIX.length);
          } else {
            riskOwnerName = await getAliasFullName(risk.riskOwner, risk.riskOwner);
          }
        }

        return {
          ...risk,
          riskOwnerName,
        };
      }),
    );

    const useTripleBraces = templateText ? /{{{.*milestonesList.*}}}/.test(templateText) : true;
    const milestonesTable = useTripleBraces
      ? generateMilestonesTable(data.milestones)
      : generateOldViewMilestonesTable(data.milestones);
    const businessOutcomesTable = generateBusinessOutcomesTable(data.businessOutcomes);
    const risksTable = generateRiskTable(processedRisks);
    const changeRequestsTable = generateCRTable(data.changeRequests);
    const currentUserAlias = await getUserAlias();
    const projectPlaceholders: PlaceholderValues = {
      businessOutcomesList: businessOutcomesTable,
      milestonesList: milestonesTable,
      risksList: risksTable,
      changeRequestsList: changeRequestsTable,
      projectName: projectData.name,
      projectManager: await getAliasFullName(data.createdBy ?? currentUserAlias),
      statusDate: data.generationDate ? data.generationDate : `${getToday().format(DATE_FORMAT.API_ONLY_DATE)}`,
    };

    return projectPlaceholders;
  };

  const updatePlaceholderValues = async (data: any) => {
    const updatedPlaceholders = await calculatePlaceholders(data, data.template);
    setPlaceholderValues(prevState => ({ ...prevState, ...updatedPlaceholders }));
  };

  return {
    placeholderValues,
    updatePlaceholderValues,
    calculatePlaceholders,
  };
};
